/**
 * Have-I-Been-Pwned compromised-password check via k-anonymity API.
 *
 * Privacy model: only the first 5 hex characters of the SHA-1 hash leave
 * the browser. HIBP returns ~500-1000 hash-suffixes matching the prefix;
 * we match locally. 20 bits of entropy leak is mathematically irreversible,
 * which is why Apple, 1Password, Google, Microsoft, Cloudflare, and the
 * UK National Cyber Security Centre all integrate this exact API.
 *
 * Per CLAUDE.md Cardinal Rule #3 (no data outside EU): we leak 20 bits of
 * a SHA-1 prefix only — not the password, not a reversible token. HIBP is
 * a UK-hosted service classified as low-risk metadata transfer.
 *
 * Failure mode: any network / parsing failure returns `{ pwned: false }`
 * silently. We never block a user from signing up because HIBP is down.
 */

export interface HibpResult {
  pwned: boolean;
  /** Number of breaches the password has appeared in. 0 when not found. */
  count: number;
}

const HIBP_RANGE_URL = "https://api.pwnedpasswords.com/range/";

/** SHA-1 hex digest via Web Crypto. Returns uppercase hex. */
async function sha1Hex(input: string): Promise<string> {
  const enc = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest("SHA-1", enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();
}

/**
 * Returns whether the password is known to be breached and the breach count.
 * Returns `{ pwned: false, count: 0 }` on any network / parse error so a
 * HIBP outage never blocks the user.
 */
export async function checkPasswordPwned(password: string): Promise<HibpResult> {
  if (!password || password.length < 8) return { pwned: false, count: 0 };
  // SubtleCrypto is unavailable outside secure contexts. Fail open.
  if (typeof crypto === "undefined" || !crypto.subtle) {
    return { pwned: false, count: 0 };
  }

  try {
    const hash = await sha1Hex(password);
    const prefix = hash.slice(0, 5);
    const suffix = hash.slice(5);
    const res = await fetch(`${HIBP_RANGE_URL}${prefix}`, {
      method: "GET",
      // Privacy-friendly mode requested by HIBP — server pads response so
      // an observer cannot infer how many matches our prefix had.
      headers: { "Add-Padding": "true" },
    });
    if (!res.ok) return { pwned: false, count: 0 };
    const body = await res.text();
    for (const line of body.split("\n")) {
      const [lineSuffix, countStr] = line.trim().split(":");
      if (!lineSuffix) continue;
      if (lineSuffix.toUpperCase() === suffix) {
        const count = parseInt(countStr ?? "0", 10);
        if (Number.isNaN(count) || count <= 0) return { pwned: false, count: 0 };
        return { pwned: true, count };
      }
    }
    return { pwned: false, count: 0 };
  } catch {
    return { pwned: false, count: 0 };
  }
}
